import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InlineLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { InitialsAvatar } from "@/components/common/InitialsAvatar";
import { Money } from "@/components/common/Money";
import { TransactionRow } from "@/components/wallet/TransactionRow";
import { supabase } from "@/lib/supabase";
import { formatDate, formatPhone } from "@/lib/format";
import type { UserRow, TransactionRow as TxRow } from "@/types/database";

function useMemberSearch(query: string) {
  return useQuery({
    queryKey: ["admin-members", query],
    enabled: query.length === 0 || query.length >= 2,
    queryFn: async (): Promise<UserRow[]> => {
      let q = supabase.from("users").select("*").order("created_at", { ascending: false }).limit(50);
      if (query.trim()) {
        const term = `%${query.trim()}%`;
        q = q.or(`first_name.ilike.${term},last_name.ilike.${term},phone.ilike.${term},email.ilike.${term},referral_code.ilike.${term}`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

function useUserTransactions(userId: string | null) {
  return useQuery({
    queryKey: ["admin-user-transactions", userId],
    enabled: !!userId,
    queryFn: async (): Promise<TxRow[]> => {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as TxRow[];
    },
  });
}

export function AdminMembers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<UserRow | null>(null);
  const { data, isLoading } = useMemberSearch(search);

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input placeholder={t("admin.membersPage.search")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
      </div>

      {isLoading ? (
        <InlineLoading />
      ) : (data?.length ?? 0) === 0 ? (
        <EmptyState title={t("common.noData")} />
      ) : (
        <div className="space-y-2">
          {data!.map((u) => (
            <Card key={u.id}>
              <CardContent
                className="py-3 flex items-center gap-3 cursor-pointer hover:bg-accent/40 transition-colors"
                onClick={() => setSelected(u)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelected(u);
                  }
                }}
              >
                <InitialsAvatar name={u.first_name} seed={u.id} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{u.first_name ?? "—"} {u.last_name ?? ""}</p>
                    {u.role !== "user" && <Badge variant="gold">{u.role}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {u.phone ? formatPhone(u.phone) : "—"} · {u.email ?? "—"}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {t("admin.membersPage.since")} {formatDate(u.created_at)} · code {u.referral_code}
                  </p>
                </div>
                <div className="text-right text-xs space-y-0.5">
                  <p><span className="text-muted-foreground">{t("admin.membersPage.wallet")}:</span> <Money amount={u.wallet_balance} compact className="font-bold" /></p>
                  <p><span className="text-muted-foreground">{t("admin.membersPage.earnings")}:</span> <Money amount={u.total_earnings} compact /></p>
                  <p><span className="text-muted-foreground">{t("admin.membersPage.referrals")}:</span> {u.paid_referrals_count}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UserDetailDialog user={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function UserDetailDialog({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const { t } = useTranslation();
  const txQ = useUserTransactions(user?.id ?? null);

  return (
    <Dialog open={!!user} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {user && (
          <>
            <DialogHeader>
              <DialogTitle>{t("admin.membersPage.detailTitle")}</DialogTitle>
            </DialogHeader>

            {/* Identity */}
            <div className="flex items-center gap-3 py-2 border-b border-white/[0.06]">
              <InitialsAvatar name={user.first_name} seed={user.id} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold truncate">{user.first_name ?? "—"} {user.last_name ?? ""}</p>
                  {user.role !== "user" && <Badge variant="gold">{user.role}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {user.phone ? formatPhone(user.phone) : "—"} · {user.email ?? "—"}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {t("admin.membersPage.since")} {formatDate(user.created_at)} · code {user.referral_code}
                </p>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-2 py-3 border-b border-white/[0.06]">
              <StatTile label={t("admin.membersPage.wallet")} value={<Money amount={user.wallet_balance} compact />} />
              <StatTile label={t("admin.membersPage.earnings")} value={<Money amount={user.total_earnings} compact />} />
              <StatTile label={t("admin.membersPage.referrals")} value={user.paid_referrals_count} />
            </div>

            {/* Transactions */}
            <div className="space-y-2 pt-2">
              <h3 className="text-sm font-semibold tracking-tight px-0.5">
                {t("admin.membersPage.transactions")}
              </h3>
              {txQ.isLoading ? (
                <InlineLoading />
              ) : (txQ.data?.length ?? 0) === 0 ? (
                <EmptyState title={t("admin.membersPage.noTransactions")} />
              ) : (
                <div className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06] overflow-hidden">
                  {txQ.data!.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-card/50 px-3 py-2 text-center">
      <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      <p className="text-base font-semibold mt-0.5 num">{value}</p>
    </div>
  );
}
